import classesK from '../kalkulatori.module.css'
import InfoBar from '../redesign/components/InfoBar'
import OddsCalculator from '../redesign/components/OddsCalculator'
import ResultsSection from '../redesign/components/ResultsSection'
import { useEffect, useState } from 'react'
import resultSectionsData from './ProjekcijaUtakmiceResultsData'

const matchMetrics = [
  'shots',
  'shotsOnTarget',
  'shotsOffTarget',
  'blockedShots',
  'freeKicks',
  'corners',
  'offsides',
  'crosses',
  'goalkeeperSaves',
  'fouls',
  'yellowCards',
  'passes',
  'accuratePasses',
  'tackles',
  'attacks',
  'dangerousAttacks',
]

const metricBaseValues = {
  shotsOnTarget: 8,
  shotsOffTarget: 10,
  blockedShots: 5,
  freeKicks: 26,
  corners: 9,
  offsides: 4,
  crosses: 36,
  goalkeeperSaves: 5,
  fouls: 22,
  yellowCards: 4,
  passes: 807,
  accuratePasses: 651,
  tackles: 23,
  attacks: 187,
  dangerousAttacks: 83,
}

const generateMetricTemplate = () => ({
  total: {
    firstHalf: '',
    fullTime: '',
  },
  home: {
    firstHalf: '',
    fullTime: '',
  },
  away: {
    firstHalf: '',
    fullTime: '',
  },
})

const calculatorType = 'Projekcija utakmice'

const ProjekcijaUtakmice = () => {
  const [disabled, setDisabled] = useState(true)
  const [showInputArea, setShowInputArea] = useState(true)

  const generateMetricsState = () => {
    const metrics = {}
    matchMetrics.forEach((metric) => {
      metrics[metric] = generateMetricTemplate()
    })
    return metrics
  }

  const getInitialData = () => ({
    values: {
      matchOutcomeOdds: ['', '', ''], // KI 1, KI X, KI 2
      goalsOdds: ['', ''], // UG 0-2, UG 3+
      teamStats: ['', '', '', '', '', ''], // DOM+, DOM-, DOM BR., GOST-, GOST+, GOST BR.
    },
    results: {
      xG: {
        total: '',
        firstHalf: '',
        secondHalf: '',
      },

      possession: {
        home: '',
        away: '',
      },

      ...generateMetricsState(),

      teamStrength: {
        home: {
          attack: '',
          defense: '',
        },
        away: {
          attack: '',
          defense: '',
        },
        combined: {
          attack: '',
          defense: '',
        },
        matchup: {
          homeAttack_vs_awayDefense: '',
          homeDefense_vs_awayAttack: '',
        },
      },
    },
  })

  const [data, setData] = useState(getInitialData)

  const getResultsFromKey = (results, key, subtitle) => {
    try {
      if (key === 'teamStrength') {
        switch (subtitle) {
          case 'Snaga domaćina':
            return [
              results.teamStrength.home.attack,
              results.teamStrength.home.defense,
            ]
          case 'Snaga gosta':
            return [
              results.teamStrength.away.attack,
              results.teamStrength.away.defense,
            ]
          case 'Snage oba tima zajedno':
            return [
              results.teamStrength.combined.attack,
              results.teamStrength.combined.defense,
            ]
          case 'Tim proseci odbrana i napada':
            return [
              results.teamStrength.matchup.homeAttack_vs_awayDefense,
              results.teamStrength.matchup.homeDefense_vs_awayAttack,
            ]
          default:
            return []
        }
      }

      // for metrics with nested structure
      if (matchMetrics.includes(key)) {
        const metric = results[key]
        return [
          metric.total.firstHalf,
          metric.total.fullTime,
          metric.home.fullTime,
          metric.home.firstHalf,
          metric.home.fullTime - metric.home.firstHalf,
          metric.away.fullTime,
          metric.away.firstHalf,
          metric.away.fullTime - metric.away.firstHalf,
        ]
      }

      // for simple structures
      return Object.values(results[key])
    } catch (e) {
      console.log(key, e)
    }
  }

  // determine if button is disabled
  useEffect(() => {
    const { matchOutcomeOdds, goalsOdds, teamStats } = data.values

    const allFilled = [...matchOutcomeOdds, ...goalsOdds, ...teamStats].every(
      (val) => val !== ''
    )

    setDisabled(!allFilled)
  }, [data.values])

  const clearFields = () => {
    setData(getInitialData())
    setShowInputArea(true)
  }

  const handleButtonClick = () => {
    if (!showInputArea) {
      clearFields()
      setDisabled(true)
      return
    } else {
      handleCalculate()
    }

    console.log(data)
    setShowInputArea((prev) => !prev)
  }

  const calculateMatchStats = (
    averageGoalsScored,
    oddsHomeWin,
    oddsDraw,
    oddsAwayWin
  ) => {
    const stats = {}

    // inverse normalization
    const impliedProbHomeWin = 1 / oddsHomeWin
    const impliedProbDraw = 1 / oddsDraw
    const impliedProbAwayWin = 1 / oddsAwayWin

    // intermediate calculations
    const overround =
      impliedProbHomeWin + impliedProbDraw + impliedProbAwayWin - 1
    const adjustedProbHomeWin = impliedProbHomeWin - overround / 3
    const adjustedProbDraw = impliedProbDraw - overround / 3

    const homeWinAdjustment = adjustedProbHomeWin * overround
    const drawAdjustment = adjustedProbDraw * overround

    const finalProbHomeWin = impliedProbHomeWin - homeWinAdjustment
    const finalProbDraw = impliedProbDraw - drawAdjustment

    const weightedImpact = finalProbHomeWin + finalProbDraw / 2
    const weightedAwayImpact = 1 - weightedImpact

    const goalsPerMatch = 2.5

    for (const metric of Object.keys(metricBaseValues)) {
      const base = metricBaseValues[metric]

      const averageMetricPerMatch = Math.round(
        (averageGoalsScored / goalsPerMatch) * base
      )

      const expectedHome = averageMetricPerMatch * weightedImpact
      const expectedAway = averageMetricPerMatch * weightedAwayImpact

      const home1H = (expectedHome * 45) / 100
      const homeFull = (expectedHome * 55) / 100 + home1H

      const away1H = (expectedAway * 45) / 100
      const awayFull = (expectedAway * 55) / 100 + away1H

      stats[metric] = {
        total: {
          firstHalf: Math.round(home1H + away1H),
          fullTime: Math.round(homeFull + awayFull),
        },
        home: {
          firstHalf: Math.round(home1H),
          fullTime: Math.round(homeFull),
        },
        away: {
          firstHalf: Math.round(away1H),
          fullTime: Math.round(awayFull),
        },
      }
    }

    const expectedShotsHome =
      stats.shotsOnTarget.home.fullTime +
      stats.shotsOffTarget.home.fullTime +
      stats.blockedShots.home.fullTime

    const expectedShotsAway =
      stats.shotsOnTarget.away.fullTime +
      stats.shotsOffTarget.away.fullTime +
      stats.blockedShots.away.fullTime

    const shotsHome1H = (expectedShotsHome * 45) / 100
    const shotsHomeFull = expectedShotsHome

    const shotsAway1H = (expectedShotsAway * 45) / 100
    const shotsAwayFull = expectedShotsAway

    stats.shots = {
      total: {
        firstHalf: Math.round(shotsHome1H + shotsAway1H),
        fullTime: Math.round(shotsHomeFull + shotsAwayFull),
      },
      home: {
        firstHalf: Math.round(shotsHome1H),
        fullTime: Math.round(shotsHomeFull),
      },
      away: {
        firstHalf: Math.round(shotsAway1H),
        fullTime: Math.round(shotsAwayFull),
      },
    }

    return stats
  }

  const handleCalculate = () => {
    const { teamStats, matchOutcomeOdds, goalsOdds } = data.values

    // DOM+, DOM-, DOM BR., GOST-, GOST+, GOST BR.
    const [
      homeScored,
      homeConceded,
      homeGamesPlayed,
      awayConceded,
      awayScored,
      awayGamesPlayed,
    ] = teamStats.map(Number)

    const [oddsHomeWin, oddsDraw, oddsAwayWin] = matchOutcomeOdds.map(Number)
    const [oddsUnder2_5, oddsOver2_5] = goalsOdds.map(Number)

    // fair outcome probabilities
    const fairHomeWin = 1 / oddsHomeWin
    const fairDraw = 1 / oddsDraw
    const fairAwayWin = 1 / oddsAwayWin
    const fairMargin = fairHomeWin + fairDraw + fairAwayWin - 1

    const adjHomeWin = fairHomeWin - fairMargin / 3
    const adjDraw = fairDraw - fairMargin / 3

    const marginHomeWin = adjHomeWin * fairMargin
    const marginDraw = adjDraw * fairMargin

    const cleanHomeWin = fairHomeWin - marginHomeWin
    const cleanDraw = fairDraw - marginDraw

    const homeGoalProb = cleanHomeWin + cleanDraw / 2
    const awayGoalProb = 1 - homeGoalProb

    // goal over/under margin adjustment
    const probUnder = 1 / oddsUnder2_5
    const probOver = 1 / oddsOver2_5
    const goalMargin = probUnder + probOver - 1
    const overAdjusted = Math.max(0, probOver - goalMargin / 2)
    const goalFactor = overAdjusted * 5

    // ratios and strength factors
    const SND = homeScored / homeGamesPlayed
    const SOD = homeConceded / homeGamesPlayed
    const SOG = awayConceded / awayGamesPlayed
    const SNG = awayScored / awayGamesPlayed

    const SODG = (SOD + SOG) / 2
    const SNDG = (SND + SNG) / 2
    const SNDSOG = (SND + SOG) / 2
    const SODSNG = (SOD + SNG) / 2

    const averageGoalsScored = Number(
      ((goalFactor + SND + SOD + SOG + SNG) / 5).toFixed(2)
    )
    const homeGoals = Number((averageGoalsScored * homeGoalProb).toFixed(2))
    const awayGoals = Number((averageGoalsScored * awayGoalProb).toFixed(2))

    const homeGoals1H = Number(((homeGoals * 45) / 100).toFixed(2))
    const homeGoals2H = Number(((homeGoals * 55) / 100).toFixed(2))
    const awayGoals1H = Number(((awayGoals * 45) / 100).toFixed(2))
    const awayGoals2H = Number(((awayGoals * 55) / 100).toFixed(2))

    const possessionHome = Number(
      ((homeGoalProb / (homeGoalProb + awayGoalProb)) * 100).toFixed(2)
    )
    const possessionAway = Number((100 - possessionHome).toFixed(2)) // ensures total = 100.00

    const statsTeam = calculateMatchStats(
      averageGoalsScored,
      oddsHomeWin,
      oddsDraw,
      oddsAwayWin
    )

    setData((prev) => ({
      ...prev,
      results: {
        ...prev.results,
        xG: {
          total: averageGoalsScored.toFixed(2),
          firstHalf: (homeGoals1H + awayGoals1H).toFixed(2),
          secondHalf: (homeGoals2H + awayGoals2H).toFixed(2),
        },
        possession: {
          home: possessionHome.toFixed(2),
          away: possessionAway.toFixed(2),
        },
        ...statsTeam,
        teamStrength: {
          home: {
            attack: SND.toFixed(2),
            defense: SOD.toFixed(2),
          },
          away: {
            attack: SOG.toFixed(2),
            defense: SNG.toFixed(2),
          },
          combined: {
            attack: SNDG.toFixed(2),
            defense: SODG.toFixed(2),
          },
          matchup: {
            homeAttack_vs_awayDefense: SNDSOG.toFixed(2),
            homeDefense_vs_awayAttack: SODSNG.toFixed(2),
          },
        },
      },
    }))
  }

  return (
    <div className={classesK.container}>
      <InfoBar calculatorType={calculatorType} onRefresh={clearFields} />

      <div className={classesK.contentContainer}>
        <div className={classesK.submitWrapper}>
          <span>Unesi nove obračunske podatke</span>
          <button onClick={handleButtonClick} disabled={disabled}>
            {showInputArea ? 'Prikaži rezultate' : 'Unesi obračunske podatke'}
          </button>
        </div>
        {showInputArea && (
          <>
            <div className={classesK.sectionTitle}>
              Upisi kvote za ishod utakmice
            </div>
            <OddsCalculator
              field={'matchOutcomeOdds'}
              data={data.values}
              setData={setData}
              labelPrefix={['KI 1', 'KI X', 'KI 2']}
              step={['0.1', '0.1', '0.1']}
              min={['0.1', '0.1', '0.1']}
              type={['number', 'number', 'number']}
              rows={3}
            />

            <div className={classesK.sectionTitle}>Upiši kvote za golove</div>
            <OddsCalculator
              field={'goalsOdds'}
              data={data.values}
              setData={setData}
              labelPrefix={['Manje od 2.5 ( UG 0-2 )', 'Više od 2.5 ( UG 3+ )']}
              rows={2}
              type={['number', 'number']}
              step={['0.1', '0.1']}
              min={['0.1', '0.1']}
            />

            <div className={classesK.sectionTitle}>
              Upiši br. golova / br. ut. za oba tima
            </div>
            <OddsCalculator
              field={'teamStats'}
              data={data.values}
              setData={setData}
              labelPrefix={[
                'DOM +  ( dom. dao golova)',
                'DOM -  ( dom. primio golova)',
                'DOM BR. ( dom. odigrao utakmica)',
                'GOST +  ( gost. dao golova)',
                'GOST -  ( gost. primio golova)',
                'GOST BR. ( gost. odigrao utakmica)',
              ]}
              type={[
                'number',
                'number',
                'number',
                'number',
                'number',
                'number',
              ]}
              step={['1', '1', '1', '1', '1', '1']}
              min={['0', '0', '1', '0', '0', '1']}
              rows={3}
            />
          </>
        )}

        {!showInputArea && (
          <div className={classesK.sectionResults}>
            {resultSectionsData.map(
              ([title, subtitle, key, names, layoutPattern], index) => (
                <ResultsSection
                  key={index}
                  title={title}
                  subtitle={subtitle}
                  results={getResultsFromKey(data.results, key, subtitle)}
                  names={names}
                  layoutPattern={layoutPattern}
                />
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjekcijaUtakmice
